import { useState, useRef, useEffect } from 'react'
import { Upload, Play, Pause, Scissors, X, Volume2, RefreshCw } from 'lucide-react'
import { useToast } from './Toast'
import { uploadFileOrFallback, fetchBlobFromUrl } from '../utils/upload'
import { useResolvedUrl } from '../utils/useResolvedUrl'
import { useI18n } from '../utils/i18n'

const API_ORIGIN = import.meta.env.VITE_API_ORIGIN || ''
const UPLOAD_API = API_ORIGIN ? `${API_ORIGIN.replace(/\/$/, '')}/api/upload` : '/api/upload'

export function AudioEditor({ value, onChange, onTrim, label }) {
  const { tx } = useI18n()
  const { toast } = useToast()
  const labelText = label || tx({ zh: '音频', en: 'Audio' })
  const [audioUrl, setAudioUrl] = useState(value || '')
  const resolvedAudioUrl = useResolvedUrl(audioUrl)
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [trimStart, setTrimStart] = useState(0)
  const [trimEnd, setTrimEnd] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [waveform, setWaveform] = useState([])
  const [isDragging, setIsDragging] = useState(false)
  
  const audioRef = useRef(null)
  const canvasRef = useRef(null)
  const fileInputRef = useRef(null)
  const audioContextRef = useRef(null)

  useEffect(() => {
    setAudioUrl(value || '')
  }, [value])

  useEffect(() => {
    if (audioUrl && audioRef.current) {
      audioRef.current.src = resolvedAudioUrl || ''
      loadWaveform(audioUrl)
    }
  }, [audioUrl, resolvedAudioUrl])

  const loadWaveform = async (url) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)()
      }
      const blob = await fetchBlobFromUrl(url)
      if (!blob) throw new Error('missing audio blob')
      const arrayBuffer = await blob.arrayBuffer()
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer)
      
      const channelData = audioBuffer.getChannelData(0)
      const samples = 100
      const blockSize = Math.floor(channelData.length / samples)
      const peaks = []
      
      for (let i = 0; i < samples; i++) {
        let sum = 0
        for (let j = 0; j < blockSize; j++) {
          sum += Math.abs(channelData[i * blockSize + j])
        }
        peaks.push(sum / blockSize)
      }
      
      const max = Math.max(...peaks)
      setWaveform(peaks.map(p => p / max))
      setDuration(audioBuffer.duration)
      setTrimEnd(audioBuffer.duration)
    } catch (e) {
      console.error('Failed to load waveform:', e)
      setWaveform([])
    }
  }

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration)
      setTrimEnd(audioRef.current.duration)
    }
  }

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
      // 如果播放到 trimEnd，停止
      if (audioRef.current.currentTime >= trimEnd) {
        audioRef.current.pause()
        setIsPlaying(false)
      }
    }
  }

  const togglePlay = () => {
    if (!audioRef.current) return
    
    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.currentTime = trimStart
      audioRef.current.play()
    }
    setIsPlaying(!isPlaying)
  }

  const handleUpload = async (file) => {
    if (!file.type.startsWith('audio/')) {
      toast({ kind: 'warning', title: tx({ zh: '提示', en: 'Hint' }), message: tx({ zh: '请选择音频文件', en: 'Please choose an audio file' }) })
      return
    }

    setUploading(true)
    try {
      const { url, mode } = await uploadFileOrFallback(file, UPLOAD_API, { allowLocalFallback: true })
      setAudioUrl(url)
      onChange(url)
      if (mode === 'local') {
        toast({
          kind: 'warning',
          title: tx({ zh: '已本地保存', en: 'Saved locally' }),
          message: tx({ zh: '上传接口暂不可用：音频已保存到本机浏览器（仅本机可用）。', en: 'Upload is unavailable: audio was saved in this browser (local only).' }),
        })
      }
    } catch (e) {
      console.error('Upload failed:', e)
      toast({ kind: 'error', title: tx({ zh: '上传失败', en: 'Upload failed' }), message: String(e?.message || e) })
    }
    setUploading(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleUpload(files[0])
    }
  }

  const handleWaveformClick = (e) => {
    if (!duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const percent = x / rect.width
    const time = percent * duration
    
    if (audioRef.current) {
      audioRef.current.currentTime = time
      setCurrentTime(time)
    }
  }

  const handleTrimDrag = (type, e) => {
    const rect = e.currentTarget.parentElement.getBoundingClientRect()
    
    const onMouseMove = (moveEvent) => {
      const x = moveEvent.clientX - rect.left
      const percent = Math.max(0, Math.min(1, x / rect.width))
      const time = percent * duration
      
      if (type === 'start') {
        setTrimStart(Math.min(time, trimEnd - 0.1))
      } else {
        setTrimEnd(Math.max(time, trimStart + 0.1))
      }
    }
    
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
    
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  const handleTrim = async () => {
    if (!audioContextRef.current || !audioUrl) return
    
    try {
      // 获取音频数据
      const blob = await fetchBlobFromUrl(audioUrl)
      if (!blob) throw new Error('missing audio blob')
      const arrayBuffer = await blob.arrayBuffer()
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer.slice(0))
      
      // 计算裁剪范围
      const sampleRate = audioBuffer.sampleRate
      const startSample = Math.floor(trimStart * sampleRate)
      const endSample = Math.floor(trimEnd * sampleRate)
      const newLength = endSample - startSample
      
      // 创建新的 AudioBuffer
      const newBuffer = audioContextRef.current.createBuffer(
        audioBuffer.numberOfChannels,
        newLength,
        sampleRate
      )
      
      // 复制裁剪后的数据
      for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
        const oldData = audioBuffer.getChannelData(channel)
        const newData = newBuffer.getChannelData(channel)
        for (let i = 0; i < newLength; i++) {
          newData[i] = oldData[startSample + i]
        }
      }
      
      // 转换为 WAV Blob
      const wavBlob = audioBufferToWav(newBuffer)
      
      // 上传裁剪后的音频
      const formData = new FormData()
      formData.append('file', wavBlob, 'trimmed.wav')
      const fileLike = new File([wavBlob], 'trimmed.wav', { type: 'audio/wav' })
      const { url, mode } = await uploadFileOrFallback(fileLike, UPLOAD_API, { allowLocalFallback: true })
      setAudioUrl(url)
      onChange(url)
      setTrimStart(0)
      setTrimEnd(newBuffer.duration)
      loadWaveform(url)
      if (typeof onTrim === 'function') onTrim(url)
      if (mode === 'local') {
        toast({
          kind: 'warning',
          title: tx({ zh: '已本地保存', en: 'Saved locally' }),
          message: tx({
            zh: '上传接口暂不可用：裁剪后的音频已保存到本机浏览器（仅本机可用）。',
            en: 'Upload is unavailable: trimmed audio was saved in this browser (local only).',
          }),
        })
      }
    } catch (e) {
      console.error('Trim failed:', e)
      toast({ kind: 'error', title: tx({ zh: '裁剪失败', en: 'Trim failed' }), message: String(e?.message || e) })
    }
  }

  // AudioBuffer 转 WAV
  const audioBufferToWav = (buffer) => {
    const numChannels = buffer.numberOfChannels
    const sampleRate = buffer.sampleRate
    const format = 1 // PCM
    const bitDepth = 16
    
    const bytesPerSample = bitDepth / 8
    const blockAlign = numChannels * bytesPerSample
    
    const dataLength = buffer.length * blockAlign
    const bufferLength = 44 + dataLength
    
    const arrayBuffer = new ArrayBuffer(bufferLength)
    const view = new DataView(arrayBuffer)
    
    // WAV header
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i))
      }
    }
    
    writeString(0, 'RIFF')
    view.setUint32(4, 36 + dataLength, true)
    writeString(8, 'WAVE')
    writeString(12, 'fmt ')
    view.setUint32(16, 16, true)
    view.setUint16(20, format, true)
    view.setUint16(22, numChannels, true)
    view.setUint32(24, sampleRate, true)
    view.setUint32(28, sampleRate * blockAlign, true)
    view.setUint16(32, blockAlign, true)
    view.setUint16(34, bitDepth, true)
    writeString(36, 'data')
    view.setUint32(40, dataLength, true)
    
    // 写入音频数据
    const channels = []
    for (let i = 0; i < numChannels; i++) {
      channels.push(buffer.getChannelData(i))
    }
    
    let offset = 44
    for (let i = 0; i < buffer.length; i++) {
      for (let channel = 0; channel < numChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, channels[channel][i]))
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true)
        offset += 2
      }
    }
    
    return new Blob([arrayBuffer], { type: 'audio/wav' })
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleClear = () => {
    setAudioUrl('')
    onChange('')
    setWaveform([])
    setDuration(0)
    setCurrentTime(0)
    setTrimStart(0)
    setTrimEnd(0)
  }

  return (
    <div className="space-y-3">
      <label className="block text-xs text-slate-500 font-bold">{labelText}</label>
      
      <audio
        ref={audioRef}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setIsPlaying(false)}
        className="hidden"
      />

      {audioUrl ? (
        <div className="bg-slate-50 rounded-3xl p-4 border-2 border-slate-100">
          {/* 波形显示 - 更高更清晰 */}
          <div 
            className="relative h-24 bg-white rounded-2xl border-2 border-slate-100 cursor-pointer overflow-hidden"
            onClick={handleWaveformClick}
          >
            {/* 波形条 - 更密集 */}
            <div className="absolute inset-0 flex items-center px-0.5">
              {waveform.map((peak, i) => (
                <div
                  key={i}
                  className="flex-1 mx-px bg-emerald-500/70 rounded-sm"
                  style={{ height: `${Math.max(peak * 90, 2)}%` }}
                />
              ))}
            </div>
            
            {/* 裁剪区域高亮 */}
            <div
              className="absolute top-0 bottom-0 bg-emerald-200/40"
              style={{
                left: `${(trimStart / duration) * 100}%`,
                right: `${100 - (trimEnd / duration) * 100}%`
              }}
            >
              {/* 左侧拖拽手柄 */}
              <div
                className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize bg-emerald-500 hover:bg-emerald-600"
                onMouseDown={(e) => handleTrimDrag('start', e)}
              >
                <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-3 h-6 bg-emerald-500 rounded-md" />
              </div>
              {/* 右侧拖拽手柄 */}
              <div
                className="absolute right-0 top-0 bottom-0 w-1 cursor-ew-resize bg-emerald-500 hover:bg-emerald-600"
                onMouseDown={(e) => handleTrimDrag('end', e)}
              >
                <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-3 h-6 bg-emerald-500 rounded-md" />
              </div>
            </div>
            
            {/* 播放位置指示器 */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-slate-700 shadow-lg"
              style={{ left: `${(currentTime / duration) * 100}%` }}
            />
            
            {/* 时间刻度 */}
            <div className="absolute bottom-0 left-0 right-0 h-4 bg-white/70 backdrop-blur flex items-center justify-between px-2 border-t border-slate-100">
              <span className="text-[10px] text-slate-400 font-mono font-bold">0:00</span>
              <span className="text-[10px] text-slate-400 font-mono font-bold">{formatTime(duration / 2)}</span>
              <span className="text-[10px] text-slate-400 font-mono font-bold">{formatTime(duration)}</span>
            </div>
          </div>

          {/* 精确裁剪控制 */}
          <div className="flex items-center gap-3 mt-3 p-3 bg-white rounded-2xl border-2 border-slate-100">
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-slate-500 font-bold">{tx({ zh: '起点', en: 'Start' })}</span>
              <input
                type="number"
                step="0.1"
                min="0"
                max={trimEnd - 0.1}
                value={trimStart.toFixed(1)}
                onChange={(e) => setTrimStart(Math.max(0, Math.min(parseFloat(e.target.value) || 0, trimEnd - 0.1)))}
                className="w-16 bg-white border-2 border-slate-100 rounded-xl px-2 py-1 text-slate-800 text-xs font-mono text-center focus:border-emerald-400 outline-none"
              />
              <span className="text-[10px] text-slate-400 font-bold">{tx({ zh: '秒', en: 's' })}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-slate-500 font-bold">{tx({ zh: '终点', en: 'End' })}</span>
              <input
                type="number"
                step="0.1"
                min={trimStart + 0.1}
                max={duration}
                value={trimEnd.toFixed(1)}
                onChange={(e) => setTrimEnd(Math.max(trimStart + 0.1, Math.min(parseFloat(e.target.value) || duration, duration)))}
                className="w-16 bg-white border-2 border-slate-100 rounded-xl px-2 py-1 text-slate-800 text-xs font-mono text-center focus:border-emerald-400 outline-none"
              />
              <span className="text-[10px] text-slate-400 font-bold">{tx({ zh: '秒', en: 's' })}</span>
            </div>
            <div className="flex-1 text-right">
              <span className="text-[10px] text-slate-500 font-bold">{tx({ zh: '时长: ', en: 'Length: ' })}</span>
              <span className="text-xs text-emerald-700 font-mono font-bold">{(trimEnd - trimStart).toFixed(1)}s</span>
            </div>
          </div>

          {/* 控制栏 */}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <button
                onClick={togglePlay}
                className="p-2 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white transition-colors border-2 border-emerald-600"
              >
                {isPlaying ? <Pause size={16} /> : <Play size={16} />}
              </button>
              <span className="text-xs text-slate-600 font-mono font-bold">
                {formatTime(currentTime)}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={handleTrim}
                disabled={trimStart === 0 && trimEnd === duration}
                className="flex items-center gap-1 px-3 py-2 rounded-2xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold transition-colors border-2 border-emerald-600"
              >
                <Scissors size={12} />
                {tx({ zh: '裁剪', en: 'Trim' })}
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1 px-3 py-2 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold transition-colors border-2 border-amber-500"
                title={tx({ zh: '替换音频', en: 'Replace audio' })}
              >
                <RefreshCw size={12} />
                {tx({ zh: '替换', en: 'Replace' })}
              </button>
              <button
                onClick={handleClear}
                className="p-2 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white transition-colors border-2 border-rose-600"
                title={tx({ zh: '删除音频', en: 'Remove audio' })}
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
          onDragLeave={(e) => { e.preventDefault(); setIsDragging(false) }}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            h-24 border-2 border-dashed rounded-2xl cursor-pointer
            flex flex-col items-center justify-center gap-2 transition-all
            ${isDragging ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-slate-300 bg-slate-50'}
            ${uploading ? 'pointer-events-none opacity-60' : ''}
          `}
        >
          {uploading ? (
            <span className="text-xs text-slate-600 font-bold">{tx({ zh: '上传中...', en: 'Uploading…' })}</span>
          ) : (
            <>
              <Volume2 size={24} className="text-slate-400" />
              <span className="text-xs text-slate-500 font-medium">{tx({ zh: '拖拽音频或点击上传', en: 'Drag audio here or click to upload' })}</span>
            </>
          )}
        </div>
      )}

      {/* 隐藏的文件输入（用于上传和替换） */}
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0])}
        className="hidden"
      />

      {/* URL 输入（无音频时） */}
      {!audioUrl && (
        <input
          type="text"
          value={audioUrl}
          onChange={(e) => {
            setAudioUrl(e.target.value)
            onChange(e.target.value)
          }}
          className="w-full bg-white border-2 border-slate-100 rounded-2xl px-3 py-2 text-slate-800 text-xs outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
          placeholder={tx({ zh: '或直接输入音频 URL', en: 'Or paste an audio URL' })}
        />
      )}
    </div>
  )
}
